import { tags as cacheTags } from './tags';

/** `shared.image-text` → `image-text` */
function componentNameFromUid(componentUid: string): string {
  const name = componentUid.split('.').pop() ?? componentUid;

  return name.replace(/_/g, '-');
}

const validateBlock = (block: unknown): boolean => {
  if (!block) return false;
  if (typeof block !== 'object') return false;
  if (!('__component' in block)) return false;

  return true;
};

/** if a page contains multipel for the same component, tags will follow:
 * `image-text-about-1, `image-text-about-2` etc */
function createNormalisedComponentTag(
  componentName: string,
  pageSlug: string,
  occurrenceIndex: number
): string {
  const normalisedPageSlug = pageSlug.replace(/^\//, '').replace(/\//g, '-');
  return `${componentName}-${normalisedPageSlug}-${occurrenceIndex + 1}`;
}

/**
 * Collects cache tags for blocks on a page,
 * so we can later shove them to the page fetch->cache tag list
 *
 *  returns an array for easier spread
 */
export function collectBlockTagsForPageEntity(
  blocks: unknown,
  pageSlug: string | null
): string[] {
  if (!pageSlug || !Array.isArray(blocks)) {
    return [];
  }

  const occurrenceCounts = new Map<string, number>();

  return blocks.flatMap((block) => {
    if (!validateBlock(block)) return [];

    const componentName = componentNameFromUid(String(block.__component));
    const occurrenceIndex = occurrenceCounts.get(componentName) ?? 0;
    occurrenceCounts.set(componentName, occurrenceIndex + 1);

    return [
      createNormalisedComponentTag(componentName, pageSlug, occurrenceIndex),
    ];
  });
}

function getHeaderFooterDocId(reference: unknown): string | null {
  if (!reference) return null;
  if (typeof reference !== 'object') return null;
  if (!('data' in reference)) return null;

  const data = reference.data;

  if (!data) return null;
  if (typeof data !== 'object') return null;
  if (!('documentId' in data)) return null;
  if (typeof data.documentId !== 'string') return null;

  return data.documentId;
}

/** Tags pages/blogs that reference a header or footer (see entityRevalidation header/footer handlers). */
export function collectLayoutTags(attributes: unknown): string[] {
  if (!attributes) return [];
  if (typeof attributes !== 'object') return [];

  const entry = attributes as Record<string, unknown>;
  const layoutTags: string[] = [];

  const headerId = getHeaderFooterDocId(entry.header_reference);
  if (headerId) layoutTags.push(cacheTags.headerByDocId(headerId));

  const footerId = getHeaderFooterDocId(entry.footer_reference);
  if (footerId) layoutTags.push(cacheTags.footerByDocId(footerId));

  return layoutTags;
}
